// src/stores/authStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authService from '../services/authService';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      refreshTokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isRefreshing: false,

        // Computed value for user permissions
        userPermissions: [],
        userRole: null,
        tokenRefreshTimer: null,
        visibilityHandler: null,
   
          // Extended actions
      initAuth: async () => {
        // try {
        //   set({ isLoading: true });
          
        //   // Check if token exists and validate it
        //   const state = get();
        //   if (state.token) {
        //     const user = await authService.getCurrentUser();
        //     set({ 
        //       user,
        //       isAuthenticated: true,
        //       isLoading: false,
        //       userPermissions: user.permissions || [],
        //       userRole: user.role || null,
        //     });
        //   } else {
        //     set({ isLoading: false });
        //   }
        // } catch (error) {
        //   // Token is invalid, clear auth state
        //   get().logout();
        // }
      },
      
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);
          console.log("🚀 ~ login: ~ response:", response)
          
          // Debug token extraction
          console.log("Token from response:", response.token);
          console.log("Full response structure:", JSON.stringify(response, null, 2));
          
          const { user, token, refreshToken, tokenExpiresAt, refreshTokenExpiresAt } = response;
          
          // Debug token state before setting
          console.log("About to set token:", token);
          console.log("About to set refreshToken:", refreshToken);
          console.log("Token expires at:", new Date(tokenExpiresAt));
          console.log("Refresh token expires at:", new Date(refreshTokenExpiresAt));
          
          set({
            user,
            token,
            refreshToken,
            tokenExpiresAt,
            refreshTokenExpiresAt,
            isAuthenticated: true,
            isLoading: false,
          });
          
          // Debug user permissions
          console.log('🔍 User after login:', {
            role: user?.role,
            permissions: user?.permissions,
            permissionsLength: user?.permissions?.length,
            userId: user?._id,
            userStructure: user
          });
          
          // Debug auth state after setting
          const state = get();
          console.log("Auth state after login - token exists:", !!state.token);
          console.log("Auth state after login - isAuthenticated:", state.isAuthenticated);
          
          // Setup token refresh timer and visibility handler
          get().setupTokenRefreshTimer();
          get().setupVisibilityHandler();
          
          return user;
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || error.response?.data?.message || 'Login failed',
          });
         console.log('error', error);
         throw error; // Re-throw the error so the component can handle it
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register(userData);
          const { user, token, refreshToken, tokenExpiresAt, refreshTokenExpiresAt } = response;
          set({
            user,
            token,
            refreshToken,
            tokenExpiresAt,
            refreshTokenExpiresAt,
            isAuthenticated: true,
            isLoading: false,
          });
          
          // Setup token refresh timer and visibility handler
          get().setupTokenRefreshTimer();
          get().setupVisibilityHandler();
          
          return user;
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || error.response?.data?.message || 'Registration failed',
          });
         console.log('error', error);
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          if (get().isAuthenticated) {
            await authService.logout();
          }
        } catch (error) {
          console.log('Logout error:', error);
        } finally {
          get().clearAuthState();
        }
      },
      
      // Clear auth state without making API call - used to break refresh token loops
      clearAuthState: () => {
        // Clear the token refresh timer if it exists
        if (get().tokenRefreshTimer) {
          clearTimeout(get().tokenRefreshTimer);
        }
        
        // Clear visibility handler if it exists
        if (get().visibilityHandler) {
          get().visibilityHandler();
        }
        
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          refreshTokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          tokenRefreshTimer: null,
          visibilityHandler: null,
        });
      },
      
      setupTokenRefreshTimer: () => {
        // Clear any existing timer
        if (get().tokenRefreshTimer) {
          clearTimeout(get().tokenRefreshTimer);
        }
        
        const tokenExpiresAt = get().tokenExpiresAt;
        if (!tokenExpiresAt) {
          console.warn('No token expiration time available');
          return;
        }
        
        const now = Date.now();
        const expiresIn = tokenExpiresAt - now;
        
        // Refresh 1 hour before expiration (or 80% of token lifetime for shorter tokens)
        const refreshBuffer = Math.min(60 * 60 * 1000, expiresIn * 0.2);
        const refreshTime = Math.max(0, expiresIn - refreshBuffer);
        
        if (refreshTime > 0) {
          console.log(`Setting token refresh timer for ${refreshTime / 1000 / 60} minutes from now`);
          const timerId = setTimeout(() => {
            console.log('Token refresh timer triggered');
            get().refreshUserToken();
          }, refreshTime);
          
          set({ tokenRefreshTimer: timerId });
        } else {
          // Token is already expired or about to expire, refresh immediately
          console.log('Token expired or about to expire, refreshing immediately');
          get().refreshUserToken();
        }
      },

      refreshUserToken: async () => {
        const currentRefreshToken = get().refreshToken;
        if (!currentRefreshToken) {
          return false;
        }

        // Add refresh lock to prevent parallel refresh attempts
        if (get().isRefreshing) {
          console.log('Token refresh already in progress');
          // Wait for the current refresh to complete rather than failing
          let retryCount = 0;
          const maxRetries = 5;
          
          while (get().isRefreshing && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 300));
            retryCount++;
          }
          
          // After waiting, if we have a valid token, consider the refresh successful
          if (get().token) {
            return true;
          }
          return false;
        }

        try {
          set({ isRefreshing: true });
          const response = await authService.refreshToken(currentRefreshToken);
          
          // Verify we got the necessary tokens back
          if (!response || !response.token || !response.refreshToken) {
            throw new Error('Invalid response from refresh token endpoint');
          }
          
          const { token, refreshToken, tokenExpiresAt, refreshTokenExpiresAt } = response;
          set({ 
            token, 
            refreshToken, 
            tokenExpiresAt,
            refreshTokenExpiresAt,
            isRefreshing: false 
          });
          
          // Reset the refresh timer with new expiration
          get().setupTokenRefreshTimer();
          
          console.log('Token refreshed successfully. New expiration:', new Date(tokenExpiresAt));
          return true;
        } catch (error) {
          console.log('Token refresh failed:', error);
          // Don't call logout() as it will try to make an API call
          // Just clear the auth state directly
          get().clearAuthState();
          return false;
        } finally {
          set({ isRefreshing: false });
        }
      },
      
      setupVisibilityHandler: () => {
        const handleVisibilityChange = async () => {
          if (!document.hidden && get().isAuthenticated) {
            // Check if token needs refresh when tab becomes visible
            const tokenExpiresAt = get().tokenExpiresAt;
            if (tokenExpiresAt) {
              const now = Date.now();
              const expiresIn = tokenExpiresAt - now;
              
              // If token expires in less than 1 hour, refresh it
              if (expiresIn < 60 * 60 * 1000) {
                console.log('Tab became visible, token expires soon, refreshing...');
                await get().refreshUserToken();
              }
            }
          }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);
        
        // Store cleanup function
        const cleanup = () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('focus', handleVisibilityChange);
        };
        
        set({ visibilityHandler: cleanup });
        
        return cleanup;
      },

      updateProfile: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const updatedUser = await authService.updateProfile(userData);
          set({
            user: updatedUser,
            isLoading: false,
          });
          return updatedUser;
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || error.response?.data?.message || 'Profile update failed',
          });
         console.log('error', error);
        }
      },
      
      // Alias for updateProfile to maintain compatibility with UserSettings component
      updateUserProfile: async (userData) => {
        return get().updateProfile(userData);
      },

      updatePassword: async (currentPassword, newPassword, confirmPassword) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.updatePassword(
            currentPassword,
            newPassword,
            confirmPassword
          );
          // Update tokens if returned
          if (response.token && response.refreshToken) {
            set({
              token: response.token,
              refreshToken: response.refreshToken,
            });
          }
          set({ isLoading: false });
          return response;
        } catch (error) {
          set({
            isLoading: false,
            error: error.message || error.response?.data?.message || 'Password update failed',
          });
         console.log('error', error);
        }
      },

      forgotPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.forgotPassword(email);
          set({ isLoading: false });
          return response;
        } catch (error) {
          console.log('Password reset request error:', error);
          const errorMessage = 
            error.response?.data?.message || 
            error.data?.message || 
            error?.message || 
            'Password reset request failed. Please try again.';
          
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error; // Re-throw for component handling
        }
      },
      
      verifyResetToken: async (token) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.verifyResetToken(token);
          set({ isLoading: false });
          return response;
        } catch (error) {
          console.log('Token verification error:', error);
          const errorMessage = 
            error.response?.data?.message || 
            error.data?.message || 
            error?.message || 
            'Invalid or expired reset token';
          
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error; // Re-throw for component handling
        }
      },

      resetPassword: async (token, password, confirmPassword) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.resetPassword(
            token,
            password,
            confirmPassword
          );
          
          // If reset successful and we get back tokens, update the auth state
          if (response && response.token && response.refreshToken) {
            const { token, refreshToken, data } = response;
            
            set({
              token, 
              refreshToken,
              user: data?.user || null,
              isAuthenticated: true,
              isLoading: false
            });
            
            // Setup token refresh timer
            get().setupTokenRefreshTimer();
          } else {
            set({ isLoading: false });
          }
          
          return response;
        } catch (error) {
          console.log('Password reset error:', error);
          const errorMessage = 
            error.response?.data?.message || 
            error.data?.message || 
            error?.message || 
            'Password reset failed. Please try again.';
          
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error; // Re-throw for component handling
        }
      },

      // Permission and access control
      hasPermission: (resource, action, scope = null) => {
        const { user } = get();
        if (!user || !user.permissions) return false;
        
        // Check if user is superadmin using roleId or permissions
        const isSuperAdmin = get().isSuperAdmin();
        if (isSuperAdmin) return true;
        
        // Check specific permission
        return user.permissions.some(p => 
          p.resource === resource && p.action === action
        );
      },
      
      // Check if user is a super admin
      isSuperAdmin: () => {
        const { user } = get();
        if (!user) return false;
        
        // Check populated roleId (new way)
        if (user.roleId) {
          if (user.roleId.key === 'superadmin') return true;
          if (user.roleId.name === 'Superadmin') return true;
        }
        
        // Permission-based check - must have DELETE permission with ALL scope on critical resources
        if (user.permissions && Array.isArray(user.permissions)) {
          const criticalResources = ['users', 'roles', 'settings', 'clients'];
          
          const hasCriticalPermissions = criticalResources.every(resource => 
            user.permissions.some(p => 
              p.resource === resource && 
              p.action === 'delete' && 
              p.scope === 'all'
            )
          );
          
          if (hasCriticalPermissions) return true;
        }
        
        return false;
      },
      
      hasClientAccess: (clientId) => {
        const { user } = get();
        if (!user) return false;
        
        // Superadmin has access to all clients
        if (get().isSuperAdmin()) return true;
        
        // Check if user has admin permissions on clients
        if (get().hasPermission('clients', 'delete', 'all')) return true;
        
        // Check if user belongs to this client
        if (user.clientId && user.clientId === clientId) return true;
        
        // Check if user is assigned to this client
        return user.assignedClients && user.assignedClients.includes(clientId);
      },


      // Method to update token (used by socket for auto-refresh)
      setToken: (newToken) => {
        set({ token: newToken });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'aghrba-auth-storage',
      partialize: (state) => {
        const partialState = {
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          tokenExpiresAt: state.tokenExpiresAt,
          refreshTokenExpiresAt: state.refreshTokenExpiresAt,
          isAuthenticated: state.isAuthenticated,
          // isRefreshing is intentionally excluded to prevent persisting in-progress state
        };
        console.log("Persisting auth state:", JSON.stringify({
          hasToken: !!partialState.token,
          hasRefreshToken: !!partialState.refreshToken,
          isAuthenticated: partialState.isAuthenticated
        }));
        return partialState;
      },
      onRehydrateStorage: (state) => {
        return (rehydratedState, error) => {
          if (error) {
            console.error("Error rehydrating auth store:", error);
          } else {
            console.log("Rehydrated auth state:", JSON.stringify({
              hasToken: !!rehydratedState?.token,
              hasRefreshToken: !!rehydratedState?.refreshToken,
              isAuthenticated: rehydratedState?.isAuthenticated
            }));
            
            // If user is authenticated after rehydration, setup handlers
            if (rehydratedState?.isAuthenticated && rehydratedState?.token) {
              // Setup token refresh timer
              rehydratedState.setupTokenRefreshTimer();
              // Setup visibility handler
              rehydratedState.setupVisibilityHandler();
            }
          }
        };
      }
    }
  )
);

export default useAuthStore;